import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Palette, X } from 'lucide-react';
import { adminAttributesApi } from '../api/catalog';
import type { ProductAttributeDto, ProductAttributeUpsertDto, ProductAttributeValueUpsertDto } from '../api/types';
import { PageHeader } from '../components/Shell';
import { ConfirmDialog, Field, Modal, PageLoader, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';

const DISPLAY_TYPES = [
  { value: 'color', label: 'Renk Seçici (Swatch / Renk Paleti)' },
  { value: 'button', label: 'Düğme / Kutu Seçim (Beden vb.)' },
  { value: 'dropdown', label: 'Açılır Liste (Dropdown)' },
  { value: 'text', label: 'Serbest Metin' },
];

export const AttributesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingAttr, setEditingAttr] = useState<ProductAttributeDto | null>(null);
  const [deleteAttr, setDeleteAttr] = useState<ProductAttributeDto | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [displayType, setDisplayType] = useState('dropdown');
  const [description, setDescription] = useState('');
  const [isFilterable, setIsFilterable] = useState(true);
  const [position, setPosition] = useState(1);
  const [isActive, setIsActive] = useState(true);
  const [values, setValues] = useState<ProductAttributeValueUpsertDto[]>([]);

  const { data: attributes, isLoading } = useQuery<ProductAttributeDto[]>({
    queryKey: ['admin-attributes'],
    queryFn: async () => {
      const res = await adminAttributesApi.getAll();
      return res || [];
    },
  });

  const openCreateModal = () => {
    setEditingAttr(null);
    setName('');
    setCode('');
    setDisplayType('dropdown');
    setDescription('');
    setIsFilterable(true);
    setPosition((attributes?.length ?? 0) + 1);
    setIsActive(true);
    setValues([{ value: '', colorCode: '#000000', position: 1 }]);
    setModalOpen(true);
  };

  const openEditModal = (attr: ProductAttributeDto) => {
    setEditingAttr(attr);
    setName(attr.name);
    setCode(attr.code);
    setDisplayType(attr.displayType || 'dropdown');
    setDescription(attr.description || '');
    setIsFilterable(attr.isFilterable);
    setPosition(attr.position);
    setIsActive(attr.isActive);
    setValues(
      attr.values?.map((v) => ({
        id: v.id,
        value: v.value,
        colorCode: v.colorCode || '#000000',
        position: v.position,
      })) || []
    );
    setModalOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async (dto: ProductAttributeUpsertDto) => {
      if (editingAttr) {
        return adminAttributesApi.update(editingAttr.id, dto);
      }
      return adminAttributesApi.create(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-attributes'] });
      toast.success(editingAttr ? 'Özellik başarıyla güncellendi.' : 'Yeni özellik başarıyla eklendi.');
      setModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.detail || 'Özellik kaydedilirken bir hata oluştu.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return adminAttributesApi.remove(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-attributes'] });
      toast.success('Özellik başarıyla silindi.');
      setDeleteAttr(null);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.detail || 'Özellik silinirken bir hata oluştu.');
    },
  });

  const handleAddValue = () => {
    setValues((prev) => [
      ...prev,
      {
        value: '',
        colorCode: displayType === 'color' ? '#000000' : undefined,
        position: prev.length + 1,
      },
    ]);
  };

  const handleRemoveValue = (idx: number) => {
    setValues((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleValueChange = (idx: number, field: keyof ProductAttributeValueUpsertDto, val: any) => {
    setValues((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Lütfen özellik adını giriniz.');
      return;
    }

    const filteredValues = values
      .filter((v) => v.value.trim().length > 0)
      .map((v, i) => ({
        ...v,
        value: v.value.trim(),
        position: i + 1,
      }));

    const dto: ProductAttributeUpsertDto = {
      name: name.trim(),
      code: code.trim() || undefined,
      displayType,
      description: description.trim() || undefined,
      isFilterable,
      position,
      isActive,
      values: filteredValues,
    };

    saveMutation.mutate(dto);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Özellikler & Nitelikler"
        description="Renk, beden, materyal vb. standart ürün özelliklerini tanımlayın ve vitrin filtrelerinde kullanın."
        actions={
          <button
            onClick={openCreateModal}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Yeni Özellik Ekle
          </button>
        }
      />

      {/* Attribute List */}
      <div className="overflow-hidden rounded-xl border border-ink-200 bg-white shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-ink-200 bg-ink-50/75 text-xs font-semibold uppercase tracking-wider text-ink-500">
              <tr>
                <th className="px-6 py-3.5">Özellik Adı</th>
                <th className="px-6 py-3.5">Kod</th>
                <th className="px-6 py-3.5">Görünüm Biçimi</th>
                <th className="px-6 py-3.5">Değerler</th>
                <th className="px-6 py-3.5">Filtrelenebilir</th>
                <th className="px-6 py-3.5">Durum</th>
                <th className="px-6 py-3.5 text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {attributes && attributes.length > 0 ? (
                attributes.map((attr: ProductAttributeDto) => (
                  <tr key={attr.id} className="hover:bg-ink-50/50 transition">
                    <td className="px-6 py-4 font-medium text-ink-900">
                      <div className="flex items-center gap-2">
                        {attr.displayType === 'color' && <Palette className="h-4 w-4 text-indigo-500" />}
                        <span>{attr.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-ink-500">
                      <code className="rounded bg-ink-100 px-2 py-0.5 text-xs font-mono">
                        {attr.code}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center rounded-md bg-ink-100 px-2.5 py-1 text-xs font-medium text-ink-700">
                        {DISPLAY_TYPES.find((d) => d.value === attr.displayType)?.label || attr.displayType}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap items-center gap-1.5 max-w-md">
                        {attr.values && attr.values.length > 0 ? (
                          attr.values.slice(0, 6).map((v) => (
                            <span
                              key={v.id}
                              className="inline-flex items-center gap-1 rounded border border-ink-200 bg-white px-2 py-0.5 text-xs text-ink-700"
                            >
                              {v.colorCode && (
                                <span
                                  className="h-2.5 w-2.5 rounded-full border border-ink-300 shadow-xs"
                                  style={{ backgroundColor: v.colorCode }}
                                />
                              )}
                              {v.value}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-ink-400">Değer tanımlanmamış</span>
                        )}
                        {attr.values && attr.values.length > 6 && (
                          <span className="text-xs text-ink-400">+{attr.values.length - 6} daha</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {attr.isFilterable ? (
                        <span className="badge bg-emerald-50 text-emerald-700">Evet</span>
                      ) : (
                        <span className="badge bg-ink-100 text-ink-600">Hayır</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {attr.isActive ? (
                        <span className="badge bg-emerald-50 text-emerald-700">Aktif</span>
                      ) : (
                        <span className="badge bg-ink-100 text-ink-600">Pasif</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(attr)}
                          className="rounded-lg p-1.5 text-ink-500 hover:bg-ink-100 hover:text-indigo-600 transition"
                          title="Düzenle"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteAttr(attr)}
                          className="rounded-lg p-1.5 text-ink-500 hover:bg-ink-100 hover:text-red-600 transition"
                          title="Sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-ink-500">
                    Henüz tanımlanmış bir özellik bulunmuyor.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {modalOpen && (
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editingAttr ? 'Özelliği Düzenle' : 'Yeni Özellik Tanımla'}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Özellik Adı" hint="Örn: Renk, Beden, Malzeme" required>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Özellik adı giriniz..."
                  className="field"
                />
              </Field>

              <Field label="Kod (Slug)" hint="Boş bırakılırsa otomatik üretilir">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="örn: renk, beden"
                  className="field font-mono"
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Görünüm Biçimi">
                <select
                  value={displayType}
                  onChange={(e) => setDisplayType(e.target.value)}
                  className="field"
                >
                  {DISPLAY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Sıralama">
                <input
                  type="number"
                  min="0"
                  value={position}
                  onChange={(e) => setPosition(parseInt(e.target.value) || 0)}
                  className="field"
                />
              </Field>
            </div>

            <div className="flex items-center gap-6 py-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-ink-700">
                <input
                  type="checkbox"
                  checked={isFilterable}
                  onChange={(e) => setIsFilterable(e.target.checked)}
                  className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                />
                Katalog filtrelerinde göster
              </label>

              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-ink-700">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                />
                Aktif
              </label>
            </div>

            {/* Values Sub-Form */}
            <div className="space-y-2 pt-2 border-t border-ink-200">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-ink-900">
                  Ön Tanımlı Değerler ({values.length})
                </h4>
                <button
                  type="button"
                  onClick={handleAddValue}
                  className="btn-secondary btn-sm inline-flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" /> Değer Ekle
                </button>
              </div>

              <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                {values.map((val, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Değer adı (örn: Siyah, M, 42)..."
                      value={val.value}
                      onChange={(e) => handleValueChange(idx, 'value', e.target.value)}
                      className="field py-1.5 flex-1"
                    />

                    {displayType === 'color' && (
                      <div className="flex items-center gap-1">
                        <input
                          type="color"
                          value={val.colorCode || '#000000'}
                          onChange={(e) => handleValueChange(idx, 'colorCode', e.target.value)}
                          className="h-8 w-8 cursor-pointer rounded border border-ink-300 p-0.5"
                          title="Renk Seç"
                        />
                        <input
                          type="text"
                          value={val.colorCode || ''}
                          onChange={(e) => handleValueChange(idx, 'colorCode', e.target.value)}
                          placeholder="#000000"
                          className="field w-20 py-1.5 text-xs font-mono"
                        />
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleRemoveValue(idx)}
                      className="rounded-lg p-1 text-ink-400 hover:bg-red-50 hover:text-red-500"
                      title="Değeri Kaldır"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-ink-200">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="btn-secondary"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={saveMutation.isPending}
                className="btn-primary inline-flex items-center gap-2"
              >
                {saveMutation.isPending && <Spinner className="h-4 w-4" />}
                {editingAttr ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteAttr && (
        <ConfirmDialog
          open={!!deleteAttr}
          onCancel={() => setDeleteAttr(null)}
          onConfirm={() => deleteMutation.mutate(deleteAttr.id)}
          title="Özelliği Sil"
          message={`"${deleteAttr.name}" özelliğini silmek istediğinizden emin misiniz? Bu özellik kategorilerden ve filtrelerden kaldırılacaktır.`}
          confirmLabel="Evet, Sil"
          danger
          busy={deleteMutation.isPending}
        />
      )}
    </div>
  );
};
